import React from 'react';
import { PhoneOutgoing } from 'lucide-react';
import { OUTBOUND_BOLETA_REASON } from '../utils/direction';

/**
 * Claves del checklist `chk_*` que comparten nombre con un criterio de la guía.
 *
 * En una llamada saliente el aviso dice que la guía de nueve criterios no
 * aplica, y tres líneas abajo el checklist mostraba "Saludo institucional ✓" en
 * verde. Para un supervisor es una contradicción sin explicación.
 *
 * Son cuatro, no dos: `sondeo` y `cierre_venta` colisionan igual que `saludo` y
 * `despedida`. Atenuar sólo las dos que se ven en verde dejaría la misma
 * contradicción latente en las otras dos el día que salgan positivas.
 *
 * No se ocultan: los `chk_*` sí se evaluaron y son detecciones secundarias
 * legítimas. Lo que se quita es la lectura de "esto es la guía".
 */
export const CHECKLIST_SOLAPA_GUIA = new Set([
  'saludo',
  'sondeo',
  'despedida',
  'cierre_venta',
]);

/**
 * Marca y explicación para llamadas salientes, donde la boleta no aplica.
 *
 * Vive en un componente compartido porque lo usan el detalle de admin
 * (`CallDetail`) y el del agente (`AgentCallDetail`), y la razón que se le
 * muestra al agente tiene que ser palabra por palabra la misma que ve su
 * supervisor: si difieren, la sesión de retroalimentación arranca discutiendo
 * la herramienta en vez de la llamada.
 */

/** Distintivo de dirección, para el encabezado del detalle. */
export function OutboundBadge() {
  return (
    <span
      title={OUTBOUND_BOLETA_REASON}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 9px',
        borderRadius: 999,
        border: '1px solid #fb923c',
        background: 'rgba(251, 146, 60, 0.12)',
        color: '#fdba74',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
        whiteSpace: 'nowrap',
      }}
    >
      <PhoneOutgoing size={12} />
      Llamada saliente
    </span>
  );
}

/** Ocupa el lugar del score y de los nueve criterios. */
export function BoletaNoAplica({ compact = false }) {
  return (
    <div
      className="card"
      style={{
        marginTop: compact ? 0 : 16,
        borderColor: '#fb923c',
        background: 'rgba(251, 146, 60, 0.06)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 6,
          color: '#fdba74',
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        <PhoneOutgoing size={14} />
        Sin calificación: llamada saliente
      </div>
      <p style={{ fontSize: 12.5, lineHeight: 1.6, color: '#c4b5fd', margin: 0 }}>
        {OUTBOUND_BOLETA_REASON} Esta llamada no recibe puntaje ni evaluación de
        los nueve criterios, y no cuenta en el promedio del agente.
      </p>
      <p style={{ fontSize: 12, lineHeight: 1.6, color: '#9ca3af', margin: '8px 0 0' }}>
        La transcripción y el resto del análisis sí están disponibles abajo, para
        consulta y validación.
      </p>
    </div>
  );
}
